import { LoaderCircle, MapPin } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { api } from "./api";
import { formatBrazilianPostalCode } from "./input-masks";

export type AddressFieldsValue = {
  postalCode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  reference: string;
};

export type AddressSuggestion = Omit<AddressFieldsValue, "number" | "complement" | "reference">;

export function addressSuggestionPatch(suggestion: AddressSuggestion): Partial<AddressFieldsValue> {
  return {
    postalCode: formatBrazilianPostalCode(suggestion.postalCode),
    street: suggestion.street,
    district: suggestion.district,
    city: suggestion.city,
    state: suggestion.state,
  };
}

export function AddressFields({
  value,
  onChange,
  layout = "form",
  streetRequired = false,
  locationRequired = false,
  autoFocusStreet = false,
}: {
  value: AddressFieldsValue;
  onChange: (patch: Partial<AddressFieldsValue>) => void;
  layout?: "form" | "editor";
  streetRequired?: boolean;
  locationRequired?: boolean;
  autoFocusStreet?: boolean;
}) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [status, setStatus] = useState<"idle" | "postal" | "street">("idle");
  const [lookupMessage, setLookupMessage] = useState("");
  const [streetSearchEnabled, setStreetSearchEnabled] = useState(false);
  const revision = useRef(0);
  const suggestionListId = useId();

  const userPatch = (patch: Partial<AddressFieldsValue>) => {
    revision.current += 1;
    setStatus("idle");
    setSuggestions([]);
    setLookupMessage("");
    onChange(patch);
  };

  const applySuggestion = (suggestion: AddressSuggestion) => {
    revision.current += 1;
    setStatus("idle");
    setStreetSearchEnabled(false);
    onChange(addressSuggestionPatch(suggestion));
    setSuggestions([]);
    setLookupMessage("Endereço preenchido. Informe o número.");
  };

  const lookupPostalCode = async (postalCode: string, requestRevision: number) => {
    setStatus("postal"); setSuggestions([]); setLookupMessage("");
    try {
      const response = await api<{ data: AddressSuggestion }>(`/address-lookup/postal-code/${postalCode}`);
      if (revision.current === requestRevision) {
        setStatus("idle");
        applySuggestion(response.data);
      }
    } catch (caught) {
      if (revision.current === requestRevision) setLookupMessage(caught instanceof Error ? caught.message : "CEP não encontrado.");
    } finally {
      if (revision.current === requestRevision) setStatus("idle");
    }
  };

  useEffect(() => {
    if (!streetSearchEnabled) return;
    const street = value.street.trim();
    const city = value.city.trim();
    const state = value.state.trim().toUpperCase();
    if (street.length < 3 || city.length < 3 || state.length !== 2) {
      setSuggestions([]);
      setStatus("idle");
      return;
    }
    const requestRevision = revision.current;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setStatus("street"); setLookupMessage("");
      try {
        const params = new URLSearchParams({ state, city, street, limit: "8" });
        const response = await api<{ data: AddressSuggestion[] }>(`/address-lookup/search?${params}`, { signal: controller.signal });
        if (revision.current === requestRevision) {
          setSuggestions(response.data);
          if (response.data.length === 0) setLookupMessage("Nenhum endereço encontrado. Continue o preenchimento manual.");
        }
      } catch (caught) {
        if (revision.current === requestRevision && !(caught instanceof DOMException && caught.name === "AbortError")) {
          setLookupMessage("Não foi possível buscar agora. Continue o preenchimento manual.");
        }
      } finally {
        if (revision.current === requestRevision) setStatus("idle");
      }
    }, 450);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [streetSearchEnabled, value.city, value.state, value.street]);

  const postalField = <label>CEP<div className="address-input-with-status"><input inputMode="numeric" autoComplete="postal-code" value={value.postalCode} onChange={(event) => {
    const postalCode = formatBrazilianPostalCode(event.target.value);
    userPatch({ postalCode });
    const digits = postalCode.replace(/\D/g, "");
    if (digits.length === 8) void lookupPostalCode(digits, revision.current);
  }} placeholder="00000-000" />{status === "postal" && <LoaderCircle className="address-lookup-spinner" size={16} aria-label="Consultando CEP" />}</div></label>;

  const streetField = <label className="address-street-field">Logradouro<div className="address-input-with-status"><input role="combobox" aria-expanded={suggestions.length > 0} aria-controls={suggestionListId} aria-autocomplete="list" autoComplete="address-line1" value={value.street} onFocus={() => setStreetSearchEnabled(true)} onChange={(event) => { setStreetSearchEnabled(true); userPatch({ street: event.target.value }); }} required={streetRequired} autoFocus={autoFocusStreet} />{status === "street" && <LoaderCircle className="address-lookup-spinner" size={16} aria-label="Buscando endereços" />}</div>{suggestions.length > 0 && <div className="address-suggestions" id={suggestionListId} role="listbox">{suggestions.map((suggestion) => <button type="button" role="option" aria-selected="false" key={`${suggestion.postalCode}:${suggestion.street}`} onMouseDown={(event) => event.preventDefault()} onClick={() => applySuggestion(suggestion)}><MapPin size={16} /><span><strong>{suggestion.street}</strong><small>{[suggestion.district, `${suggestion.city}/${suggestion.state}`, formatBrazilianPostalCode(suggestion.postalCode)].filter(Boolean).join(" · ")}</small></span></button>)}</div>}</label>;

  const numberField = <label>Número<input autoComplete="address-line2" value={value.number} onChange={(event) => userPatch({ number: event.target.value })} placeholder="s/n" /></label>;
  const remainingFields = <>
    <label>Complemento<input value={value.complement} onChange={(event) => userPatch({ complement: event.target.value })} /></label>
    <label>Bairro<input autoComplete="address-level3" value={value.district} onChange={(event) => userPatch({ district: event.target.value })} required={locationRequired} /></label>
    <label>Cidade<input autoComplete="address-level2" value={value.city} onChange={(event) => userPatch({ city: event.target.value })} required={locationRequired} /></label>
    <label>UF<input autoComplete="address-level1" value={value.state} onChange={(event) => userPatch({ state: event.target.value.replace(/[^a-z]/gi, "").toUpperCase().slice(0, 2) })} maxLength={2} required={locationRequired} /></label>
    <label className={layout === "form" ? "field-span-2" : "is-wide"}>Ponto de referência<input value={value.reference} onChange={(event) => userPatch({ reference: event.target.value })} /></label>
  </>;

  return <>
    {layout === "form" ? <div className="address-line">{postalField}{streetField}{numberField}</div> : <>{postalField}{streetField}{numberField}</>}
    {remainingFields}
    {lookupMessage && <small className={`${layout === "form" ? "field-span-2" : "is-wide"} address-lookup-message`} role="status">{lookupMessage}</small>}
  </>;
}
